import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { Values } from '@lib/interfaces/form';
import _get from 'lodash/get';
import Typography from '@lib/components/Typography/Typography';
import { Steps } from '@lib/enums/form';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import {
  getOptionShape,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import styles from '@lib/assets/styles/Form.module.scss';
import { GetLoadOptions } from '@lib/components/Select/types';
import UserOption from '@lib/components/Select/components/UserOption';
import AreaOption from '@lib/components/Select/components/AreaOption';
import AssetOption from '@lib/components/Select/components/AssetOption';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import AssetSingleValue from '@lib/components/Select/components/AssetSingleValue';
import ChipSingleValue from '@lib/components/Select/components/ChipSingleValue';
import { FieldNames } from 'routes/Task/enums';

type TaskFieldsArgs = {
  canQrCodeRelatedInfoUpdate?: boolean;
  getApproversLoadOptions: GetLoadOptions;
  getAssetsLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getTemplateVersionsLoadOptions?: GetLoadOptions;
  getTemplatesLoadOptions?: GetLoadOptions;
  isCreateForm?: boolean;
  onCreateNewFormTemplate?: () => void;
  onViewFormTemplate?: (
    templateFormId: string,
    templateFormVersionId: string,
  ) => void;
  step?: Steps;
  t: TFunction<'translation', undefined>;
  isCreate: boolean;
};

const defaultSelectEntityBySiteIdProps = {
  isDisabledFn: (values: Values) =>
    _get(values, `${FieldNames.site}.value`) === undefined,
  getFieldKeyFn: (values: Values, name?: string) => {
    const selectedSiteId = _get(values, `${FieldNames.site}.value`);
    if (selectedSiteId) return `${name}-site-${selectedSiteId}`;
    return FieldNames.site;
  },
};

export default function getDisabledTaskScopeFields(args: TaskFieldsArgs) {
  const {
    canQrCodeRelatedInfoUpdate = true,
    getApproversLoadOptions,
    getAssetsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    step = Steps.step1,
    t,
  } = args;
  return [
    {
      name: FieldNames.assignationsTitle,
      customRender: () => (
        <Typography variant="h3" className={styles.blockStart}>
          {t('task-scope')}
        </Typography>
      ),
      step,
    },
    {
      name: FieldNames.site,
      label: t('site'),
      element: Select,
      validation: getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getSitesLoadOptions,
        defaultOptions: false,
        components: {
          SingleValue: ChipSingleValue,
        },
        disabled: true,
        disabledTooltipProps: {
          body: t('disabled-field-desc-for-task-site-area-asset'),
        },
      } as SelectProps,
      isOptionalFn: () => false,
      step,
    },
    {
      name: FieldNames.siteArea,
      label: t('area'),
      element: Select,
      validation: getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getSiteAreasLoadOptions,
        defaultOptions: false,
        components: {
          Option: AreaOption,
          SingleValue: ChipSingleValue,
        },
        disabled: true,
        disabledTooltipProps: {
          body: canQrCodeRelatedInfoUpdate
            ? t('choose-site-first')
            : t('disabled-field-desc-for-task-site-area-asset'),
        },
      } as SelectProps,
      isVisibleFn: (values: Values) => {
        return _get(values, [FieldNames.siteArea, 'value']);
      },
      isOptionalFn: () => false,
      step,
    },
    {
      name: FieldNames.asset,
      label: t('asset'),
      element: Select,
      validation: getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: getAssetsLoadOptions,
        defaultOptions: false,
        components: {
          Option: AssetOption,
          SingleValue: AssetSingleValue,
        } as unknown as SelectComponents,
        disabled: true,
        disabledTooltipProps: {
          body: canQrCodeRelatedInfoUpdate
            ? t('choose-site-area-first')
            : t('disabled-field-desc-for-task-site-area-asset'),
        },
      } as SelectProps,
      isVisibleFn: (values: Values) => {
        return _get(values, [FieldNames.asset, 'value']);
      },
      isOptionalFn: () => false,
      step,
    },
    {
      name: FieldNames.approver,
      label: t('approver'),
      element: Select,
      validation: Yup.object()
        .shape(getOptionShape(t, true))
        .required(t('required-field-error')),
      componentProps: {
        isAsync: true,
        getLoadOptions: getApproversLoadOptions,
        defaultOptions: false,
        components: {
          Option: UserOption,
          SingleValue: UserSingleValue,
        } as unknown as SelectComponents,
        disabledTooltipProps: {
          body: t('choose-site-first'),
        },
      } as SelectProps,
      ...defaultSelectEntityBySiteIdProps,
      step,
    },
  ];
}
